import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Formik, Field, useFormikContext } from 'formik'
import { Form, Row, Col, Button, Card, Divider } from 'antd'
import TextField from '../../../shared/TextField'
import TextareaField from '../../../shared/TextareaField'
import { samDataSchema } from '../../../../schema/samData'

const FormikStateSpy = ({ onDirtyChange }) => {
  const { dirty } = useFormikContext()

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(dirty)
    }
  }, [dirty, onDirtyChange])

  return null
}

const SamDataForm = ({
  initialValues,
  onSubmit,
  isSubmitAllowed,
  innerRef,
  onDirtyChange,
}) => {
  const defaultInitialValues = {
    email_subject: '',
    email_body: '',
    email_remainder_interval: '',
    email_remainder_emails_number: '',
    email_remainder_email_subject: '',
    email_remainder_email_body: '',
    consent_copy_header: '',
    consent_copy_body_block_1: '',
    consent_copy_body_block_2: '',
    consent_copy_body_block_3: '',
    consent_copy_body_block_4: '',
    consent_copy_body_block_5: '',
    finalize_copy_header: '',
    finalize_copy_body: '',
  }

  return (
    <Card className="sam-data-info-card">
      <Formik
        innerRef={innerRef}
        initialValues={{
          ...defaultInitialValues,
          ...initialValues,
        }}
        validationSchema={samDataSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ handleSubmit, dirty, setTouched, validateForm }) => {
          const handleCustomSubmit = async () => {
            const errors = await validateForm()
            if (Object.keys(errors).length > 0) {
              setTouched(
                Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
              )
            }
            handleSubmit()
          }

          return (
            <Form
              onFinish={handleCustomSubmit}
              className="user-form sam-data-form"
              layout="vertical"
            >
              <FormikStateSpy onDirtyChange={onDirtyChange} />

              <Row justify="center">
                <Col>
                  <div className="user-form__title sam-data-form__title">SAM Data</div>
                </Col>
              </Row>

              <Row gutter={24}>
                {/* --- LEFT COLUMN --- */}
                <Col span={18}>
                  <Divider orientation="left" className="form-divider">
                    Email
                  </Divider>
                  <Row gutter={24}>
                    <Col span={24}>
                      <Field
                        component={TextField}
                        label="Subject"
                        name="email_subject"
                        required
                      />
                    </Col>
                    <Col span={24}>
                      <Field
                        component={TextareaField}
                        label="Body"
                        name="email_body"
                        rows={6}
                        required
                      />
                    </Col>
                  </Row>

                  <Divider orientation="left" className="form-divider">
                    Email Reminders
                  </Divider>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Field
                        component={TextField}
                        label="Reminders interval (days)"
                        name="email_remainder_interval"
                        required
                      />
                    </Col>
                    <Col span={12}>
                      <Field
                        component={TextField}
                        label="Max number of reminder emails"
                        name="email_remainder_emails_number"
                        required
                      />
                    </Col>
                    <Col span={24}>
                      <Field
                        component={TextField}
                        label="Email Subject"
                        name="email_remainder_email_subject"
                        required
                      />
                    </Col>
                    <Col span={24}>
                      <Field
                        component={TextareaField}
                        label="Email Body"
                        name="email_remainder_email_body"
                        rows={6}
                        required
                      />
                    </Col>
                  </Row>

                  <Divider orientation="left" className="form-divider">
                    Finalize Copy
                  </Divider>
                  <Row gutter={24}>
                    <Col span={24}>
                      <Field
                        component={TextField}
                        label="Header"
                        name="finalize_copy_header"
                        required
                      />
                    </Col>
                    <Col span={24}>
                      <Field
                        component={TextareaField}
                        label="Body"
                        name="finalize_copy_body"
                        rows={3}
                        required
                      />
                    </Col>
                  </Row>
                </Col>

                {/* --- RIGHT COLUMN --- */}
                <Col span={6}>
                  <Divider orientation="left" className="form-divider">
                    Consent Copy
                  </Divider>
                  <Row gutter={24}>
                    <Col span={24}>
                      <Field
                        component={TextField}
                        label="Header"
                        name="consent_copy_header"
                        required
                      />
                    </Col>
                    <Col span={24} className="sam-data-form__consent_copy_block">
                      <Field
                        component={TextareaField}
                        label="Body: block 1"
                        name="consent_copy_body_block_1"
                        rows={5}
                        required
                      />
                    </Col>
                    <Col span={24} className="sam-data-form__consent_copy_block">
                      <Field
                        component={TextareaField}
                        label="Body: block 2"
                        name="consent_copy_body_block_2"
                        rows={5}
                        required
                      />
                    </Col>
                    <Col span={24} className="sam-data-form__consent_copy_block">
                      <Field
                        component={TextareaField}
                        label="Body: block 3"
                        name="consent_copy_body_block_3"
                        rows={5}
                        required
                      />
                    </Col>
                    <Col span={24}>
                      <Field
                        component={TextareaField}
                        label="Body: block 4"
                        name="consent_copy_body_block_4"
                        rows={5}
                        required
                      />
                    </Col>
                    <Col span={24}>
                      <Field
                        component={TextareaField}
                        label="Body: block 5"
                        name="consent_copy_body_block_5"
                        rows={5}
                        required
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              {isSubmitAllowed && (
                <Button
                  type="primary"
                  ghost
                  htmlType="submit"
                  disabled={!dirty}
                  style={{ marginTop: '8px' }}
                >
                  Save Changes
                </Button>
              )}
            </Form>
          )
        }}
      </Formik>
    </Card>
  )
}

SamDataForm.propTypes = {
  initialValues: PropTypes.shape({
    email_subject: PropTypes.string,
    email_body: PropTypes.string,
    email_remainder_interval: PropTypes.number,
    email_remainder_remainder_emails_number: PropTypes.number,
    email_remainder_email_subject: PropTypes.string,
    email_remainder_email_body: PropTypes.string,
    consent_copy_header: PropTypes.string,
    consent_copy_body_block_1: PropTypes.string,
    consent_copy_body_block_2: PropTypes.string,
    consent_copy_body_block_3: PropTypes.string,
    consent_copy_body_block_4: PropTypes.string,
    consent_copy_body_block_5: PropTypes.string,
    finalize_copy_header: PropTypes.string,
    finalize_copy_body: PropTypes.string,
  }),
  onSubmit: PropTypes.func.isRequired,
  isSubmitAllowed: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onDirtyChange: PropTypes.func,
}

SamDataForm.defaultProps = {
  initialValues: {},
  isSubmitAllowed: true,
  innerRef: null,
  onDirtyChange: () => {},
}

export default SamDataForm
